import React, { useState, useEffect } from "react";
import { TextField, Box, Dialog, Button } from "@mui/material";
import { Link2 } from "lucide-react";
import { createRoot } from "react-dom/client";
import useIsMobile from "../hooks/useIsMobile";

// URL Input Dialog Component
interface URLInputDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (url: string) => void;
  initialUrl?: string;
}

const URLInputDialog = ({
  open,
  onClose,
  onConfirm,
  initialUrl = "",
}: URLInputDialogProps) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setError("");
    }
  }, [open, initialUrl]);

  const validateUrl = (inputUrl: string): boolean => {
    if (!inputUrl.trim()) {
      setError("URL is required");
      return false;
    }

    try {
      const urlToTest = inputUrl.startsWith("http")
        ? inputUrl
        : `https://${inputUrl}`;
      new URL(urlToTest);
      setError("");
      return true;
    } catch {
      setError("Please enter a valid URL");
      return false;
    }
  };

  const handleConfirm = () => {
    if (validateUrl(url)) {
      const finalUrl = url.startsWith("http") ? url : `https://${url}`;
      onConfirm(finalUrl);
      handleClose();
    }
  };

  const handleClose = () => {
    setUrl("");
    setError("");
    onClose();
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.target.value;
    setUrl(newUrl);

    if (error && newUrl.trim()) {
      setError("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          p: 3,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: isMobile ? "stretch" : "center",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <TextField
          autoFocus
          fullWidth
          placeholder="Type or paste a link"
          value={url}
          onChange={handleUrlChange}
          error={!!error}
          helperText={error}
          InputProps={{
            startAdornment: (
              <Link2 style={{ color: "action.active", marginRight: "8px" }} />
            ),
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleConfirm();
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        />
        <Button
          variant="text"
          onClick={handleConfirm}
          disabled={url.trim().length === 0 || !!error}
          disableRipple={true}
          sx={{
            textTransform: "none",
            color: "#198282",
            minWidth: "80px",
            alignSelf: isMobile ? "flex-end" : "auto",
            "& .MuiTouchRipple-root": {
              color: "transparent !important",
            },
          }}
        >
          Apply
        </Button>
      </Box>
    </Dialog>
  );
};

// Dialog management utilities
let dialogContainer: HTMLDivElement | null = null;
let dialogRoot: any = null;

export const showURLDialog = (
  initialUrl: string,
  onConfirm: (url: string) => void,
) => {
  // Create container if it doesn't exist
  if (!dialogContainer) {
    dialogContainer = document.createElement("div");
    dialogContainer.id = "url-dialog-container";
    document.body.appendChild(dialogContainer);
    dialogRoot = createRoot(dialogContainer);
  }

  const handleClose = () => {
    if (dialogRoot && dialogContainer) {
      dialogRoot.render(null);
    }
  };

  const handleConfirm = (url: string) => {
    onConfirm(url);
    handleClose();
  };

  // Render the dialog
  dialogRoot.render(
    <URLInputDialog
      open={true}
      onClose={handleClose}
      onConfirm={handleConfirm}
      initialUrl={initialUrl}
    />,
  );
};

export default URLInputDialog;
